/**
 * Recently Viewed Products
 * Tracks and displays products the customer has recently viewed
 */

class RecentlyViewed {
  constructor(options = {}) {
    this.storageKey = 'tcg_recently_viewed';
    this.maxProducts = options.maxProducts || 20;
    this.expiryDays = options.expiryDays || 30;
    this.currentProductHandle = options.currentProductHandle || null;

    this.init();
  }

  init() {
    // Clean expired products on init
    this.cleanExpired();
  }

  /**
   * Get all recently viewed products
   * @returns {Array} Array of product objects
   */
  getProducts() {
    try {
      const data = localStorage.getItem(this.storageKey);
      return data ? JSON.parse(data) : [];
    } catch (e) {
      console.error('RecentlyViewed: Error reading from localStorage', e);
      return [];
    }
  }

  /**
   * Save products to localStorage
   * @param {Array} products - Array of product objects
   */
  saveProducts(products) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(products));
    } catch (e) {
      console.error('RecentlyViewed: Error saving to localStorage', e);
    }
  }

  /**
   * Add a product to recently viewed
   * @param {Object} product - Product data object
   */
  addProduct(product) {
    if (!product || !product.handle) return;

    const products = this.getProducts();

    // Remove if already exists (will be re-added at front)
    const filtered = products.filter(p => p.handle !== product.handle);

    // Add to front with timestamp
    filtered.unshift({
      handle: product.handle,
      title: product.title,
      url: product.url,
      image: product.image,
      price: product.price,
      compareAtPrice: product.compareAtPrice,
      available: product.available,
      vendor: product.vendor,
      timestamp: Date.now()
    });

    // Keep only max products
    const trimmed = filtered.slice(0, this.maxProducts);

    this.saveProducts(trimmed);
  }

  /**
   * Remove expired products (older than expiryDays)
   */
  cleanExpired() {
    const products = this.getProducts();
    const expiryMs = this.expiryDays * 24 * 60 * 60 * 1000;
    const now = Date.now();

    const valid = products.filter(p => {
      return (now - p.timestamp) < expiryMs;
    });

    if (valid.length !== products.length) {
      this.saveProducts(valid);
    }
  }

  /**
   * Get products for display (excludes current product)
   * @param {number} limit - Max products to return
   * @returns {Array} Array of product objects
   */
  getDisplayProducts(limit = 8) {
    const products = this.getProducts();

    // Filter out current product
    const filtered = this.currentProductHandle
      ? products.filter(p => p.handle !== this.currentProductHandle)
      : products;

    return filtered.slice(0, limit);
  }

  /**
   * Clear all recently viewed products
   */
  clear() {
    localStorage.removeItem(this.storageKey);
  }

  /**
   * Check if there are any products to display
   * @returns {boolean}
   */
  hasProducts() {
    const products = this.getDisplayProducts(1);
    return products.length > 0;
  }
}

/**
 * Render recently viewed products into a container
 */
class RecentlyViewedRenderer {
  constructor(container, options = {}) {
    this.container = typeof container === 'string'
      ? document.querySelector(container)
      : container;

    if (!this.container) return;

    this.options = {
      maxProducts: parseInt(this.container.dataset.maxProducts) || options.maxProducts || 8,
      columnsDesktop: parseInt(this.container.dataset.columnsDesktop) || options.columnsDesktop || 4,
      columnsMobile: parseInt(this.container.dataset.columnsMobile) || options.columnsMobile || 2,
      currentProductHandle: this.container.dataset.currentProduct || options.currentProductHandle || null,
      showVendor: this.container.dataset.showVendor === 'true',
      showPrice: this.container.dataset.showPrice !== 'false',
      imageRatio: this.container.dataset.imageRatio || 'square',
      ...options
    };

    this.recentlyViewed = new RecentlyViewed({
      currentProductHandle: this.options.currentProductHandle
    });

    this.render();
  }

  render() {
    const products = this.recentlyViewed.getDisplayProducts(this.options.maxProducts);

    if (products.length === 0) {
      // Hide the entire section if no products
      const section = this.container.closest('.recently-viewed-section');
      if (section) {
        section.style.display = 'none';
      }
      return;
    }

    // Show section if hidden
    const section = this.container.closest('.recently-viewed-section');
    if (section) {
      section.style.display = '';
    }

    // Build grid HTML
    const gridHtml = products.map(product => this.renderProductCard(product)).join('');

    this.container.innerHTML = `
      <div class="recently-viewed-grid" style="
        display: grid;
        grid-template-columns: repeat(${this.options.columnsMobile}, 1fr);
        gap: 16px;
      ">
        ${gridHtml}
      </div>
    `;

    // Add responsive styles
    this.addResponsiveStyles();
  }

  renderProductCard(product) {
    const imageRatioClass = `recently-viewed-card__image--${this.options.imageRatio}`;
    const priceHtml = this.options.showPrice ? this.renderPrice(product) : '';
    const vendorHtml = this.options.showVendor && product.vendor
      ? `<div class="recently-viewed-card__vendor">${this.escapeHtml(product.vendor)}</div>`
      : '';

    const availabilityClass = product.available === false ? 'recently-viewed-card--sold-out' : '';
    const soldOutBadge = product.available === false
      ? '<span class="recently-viewed-card__badge recently-viewed-card__badge--sold-out">Sold Out</span>'
      : '';

    return `
      <div class="recently-viewed-card ${availabilityClass}">
        <a href="${product.url}" class="recently-viewed-card__link">
          <div class="recently-viewed-card__image-wrapper ${imageRatioClass}">
            ${product.image
              ? `<img
                  src="${product.image}"
                  alt="${this.escapeHtml(product.title)}"
                  loading="lazy"
                  class="recently-viewed-card__image"
                />`
              : `<div class="recently-viewed-card__placeholder">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                    <rect x="3" y="3" width="18" height="18" rx="2"/>
                    <circle cx="8.5" cy="8.5" r="1.5"/>
                    <path d="M21 15l-5-5L5 21"/>
                  </svg>
                </div>`
            }
            ${soldOutBadge}
          </div>
          <div class="recently-viewed-card__info">
            ${vendorHtml}
            <h4 class="recently-viewed-card__title">${this.escapeHtml(product.title)}</h4>
            ${priceHtml}
          </div>
        </a>
      </div>
    `;
  }

  renderPrice(product) {
    if (!product.price) return '';

    const hasComparePrice = product.compareAtPrice && product.compareAtPrice > product.price;

    if (hasComparePrice) {
      return `
        <div class="recently-viewed-card__price">
          <span class="recently-viewed-card__price--sale">${this.formatMoney(product.price)}</span>
          <span class="recently-viewed-card__price--compare"><s>${this.formatMoney(product.compareAtPrice)}</s></span>
        </div>
      `;
    }

    return `
      <div class="recently-viewed-card__price">
        <span class="recently-viewed-card__price--regular">${this.formatMoney(product.price)}</span>
      </div>
    `;
  }

  formatMoney(cents) {
    // Use Shopify's money format if available
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }

    // Fallback formatting
    const amount = (cents / 100).toFixed(2);
    const currencySymbol = window.Shopify?.currency?.active || '£';
    return `${currencySymbol}${amount}`;
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  addResponsiveStyles() {
    const styleId = 'recently-viewed-responsive-styles';
    if (document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @media screen and (min-width: 750px) {
        .recently-viewed-grid {
          grid-template-columns: repeat(${Math.min(this.options.columnsDesktop, 3)}, 1fr) !important;
        }
      }
      @media screen and (min-width: 990px) {
        .recently-viewed-grid {
          grid-template-columns: repeat(${this.options.columnsDesktop}, 1fr) !important;
        }
      }
    `;
    document.head.appendChild(style);
  }
}

/**
 * Track a product view - call this on product pages
 */
function trackProductView(productData) {
  const rv = new RecentlyViewed();
  rv.addProduct(productData);
}

/**
 * Initialize all recently viewed containers on the page
 */
function initRecentlyViewed() {
  const containers = document.querySelectorAll('[data-recently-viewed-products]');
  containers.forEach(container => {
    if (!container.dataset.initialized) {
      new RecentlyViewedRenderer(container);
      container.dataset.initialized = 'true';
    }
  });
}

// Auto-initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initRecentlyViewed);
} else {
  initRecentlyViewed();
}

// Re-initialize for Shopify theme editor
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', initRecentlyViewed);
}

// Export for use in other scripts
window.RecentlyViewed = RecentlyViewed;
window.RecentlyViewedRenderer = RecentlyViewedRenderer;
window.trackProductView = trackProductView;
window.initRecentlyViewed = initRecentlyViewed;
